import { Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { GuidedTourService, TourStep, TourState } from '../../services/guided-tour.service';

@Component({
  selector: 'app-tour-overlay',
  imports: [CommonModule],
  templateUrl: './tour-overlay.component.html',
  styleUrls: ['./tour-overlay.component.scss']
})
export class TourOverlayComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('tourTooltip') tourTooltip!: ElementRef<HTMLDivElement>;
  
  private destroy$ = new Subject<void>();
  private isBrowser: boolean;
  
  tourState: TourState | null = null;
  currentStep: TourStep | null = null;
  targetElement: HTMLElement | null = null;
  private positionRetryCount: number = 0; // Track positioning retries
  
  constructor(
    private guidedTourService: GuidedTourService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    console.log('🎯 TourOverlay: ngOnInit called');
    
    // Subscribe to tour state changes
    this.guidedTourService.tourState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        console.log('🎯 TourOverlay: Received tour state:', state);
        this.tourState = state;
        this.currentStep = this.guidedTourService.getCurrentStep();
        console.log('🎯 TourOverlay: Current step:', this.currentStep);
        
        if (state.isActive && this.currentStep) {
          console.log('🎯 TourOverlay: Positioning tooltip for step:', this.currentStep.id);
          this.positionRetryCount = 0; // Reset retry counter for new step
          setTimeout(() => this.positionTooltip(), 100);
        } else {
          console.log('🎯 TourOverlay: Removing highlights');
          this.removeHighlight();
        }
      });

    // Handle window resize and scroll to reposition spotlight
    if (this.isBrowser) {
      const repositionHandler = () => {
        if (this.tourState?.isActive && this.currentStep) {
          // For iframe targets, we need to recreate the virtual element
          if (this.currentStep.iframeSelector) {
            document.querySelectorAll('.tour-iframe-target').forEach(el => el.remove());
          }
          setTimeout(() => this.positionTooltip(), 100);
        }
      };

      window.addEventListener('resize', repositionHandler);
      window.addEventListener('scroll', repositionHandler, true); // Use capture to handle all scroll events
      
      // Also listen for iframe scroll events if targeting iframe content
      const checkIframeScroll = () => {
        if (this.tourState?.isActive && this.currentStep?.iframeSelector) {
          const iframe = document.querySelector(this.currentStep.iframeSelector) as HTMLIFrameElement;
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.addEventListener('scroll', repositionHandler);
            iframe.contentWindow.addEventListener('resize', repositionHandler);
          }
        }
      };
      
      // Check for iframe events after a delay to ensure iframe is loaded
      setTimeout(checkIframeScroll, 500);
    }
  }

  ngAfterViewInit(): void {
    // Initial positioning if tour is already active
    if (this.tourState?.isActive && this.currentStep) {
      setTimeout(() => this.positionTooltip(), 100);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Position the tooltip relative to the target element
   */
  private positionTooltip(): void {
    console.log('🎯 TourOverlay: positionTooltip called');
    console.log('🎯 TourOverlay: isBrowser:', this.isBrowser);
    console.log('🎯 TourOverlay: currentStep:', this.currentStep);
    console.log('🎯 TourOverlay: tourTooltip:', !!this.tourTooltip);
    
    if (!this.isBrowser || !this.currentStep || !this.tourTooltip) return;

    // Find the target element (with iframe support)
    this.targetElement = this.findTargetElement();
    
    console.log('🎯 TourOverlay: Target element for', this.currentStep.target, ':', this.targetElement);
    
    if (!this.targetElement) {
      console.warn('🎯 TourOverlay: Target element not found:', this.currentStep.target);
      
      // Use fallback positioning instead of retrying
      this.useFallbackPositioning();
      return;
    }

    const tooltip = this.tourTooltip.nativeElement;
    const targetRect = this.targetElement.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    
    // Calculate position based on the specified position
    let top = 0;
    let left = 0;
    const spacing = 24; // Increased spacing for better separation
    
    switch (this.currentStep.position) {
      case 'top':
        top = targetRect.top - tooltipRect.height - spacing;
        left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
        break;
      case 'bottom':
        top = targetRect.bottom + spacing;
        left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
        break;
      case 'left':
        top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
        left = targetRect.left - tooltipRect.width - spacing;
        break;
      case 'right':
        top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
        left = targetRect.right + spacing;
        break;
    }

    // Ensure tooltip stays within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    if (left < 10) left = 10;
    if (left + tooltipRect.width > viewportWidth - 10) {
      left = viewportWidth - tooltipRect.width - 10;
    }
    
    if (top < 10) top = 10;
    if (top + tooltipRect.height > viewportHeight - 10) {
      top = viewportHeight - tooltipRect.height - 10;
    }

    // Apply positioning
    tooltip.style.position = 'fixed';
    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
    tooltip.style.zIndex = '10000';

    // Create spotlight effect and highlight target
    this.createSpotlight();
    this.highlightTarget();
  }

  /**
   * Use fallback positioning when target element is not found
   */
  private useFallbackPositioning(): void {
    console.log('🎯 TourOverlay: Using fallback positioning...');
    
    if (!this.tourTooltip) return;
    
    const tooltip = this.tourTooltip.nativeElement;
    
    // Position tour in a default location (center of screen)
    tooltip.style.position = 'fixed';
    tooltip.style.top = '50%';
    tooltip.style.left = '50%';
    tooltip.style.transform = 'translate(-50%, -50%)';
    tooltip.style.zIndex = '10000';
    
    // Remove any existing spotlight since we don't have a target
    this.removeHighlight();
    
    console.log('🎯 TourOverlay: Fallback positioning applied');
  }

  /**
   * Find the target element
   */
  private findTargetElement(): HTMLElement | null {
    if (!this.currentStep) return null;

    // Regular element targeting
    const element = document.querySelector(this.currentStep.target) as HTMLElement;
    
    // CRITICAL FIX: Ensure the element is actually visible
    if (element) {
      const isVisible = element.offsetWidth > 0 && 
        element.offsetHeight > 0 && 
        getComputedStyle(element).display !== 'none';
      
      if (!isVisible) {
        console.warn('🎯 TourOverlay: Target element found but not visible:', this.currentStep.target);
        return null;
      }
    }
    
    return element;
  }

  /**
   * Find target element inside an iframe
   */
  private findIframeTargetElement(): HTMLElement | null {
    if (!this.currentStep?.iframeSelector || !this.currentStep?.iframeTarget) return null;

    const iframe = document.querySelector(this.currentStep.iframeSelector) as HTMLIFrameElement;
    if (!iframe) {
      console.warn('🎯 TourOverlay: Iframe not found:', this.currentStep.iframeSelector);
      return null;
    }

    try {
      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDocument) {
        console.warn('🎯 TourOverlay: Cannot access iframe content');
        return null;
      }

      // Wait for iframe to be fully loaded
      if (iframeDocument.readyState !== 'complete') {
        console.log('🎯 TourOverlay: Iframe not fully loaded, waiting...');
        iframe.addEventListener('load', () => {
          setTimeout(() => this.positionTooltip(), 200);
        }, { once: true });
        return null;
      }

      // CRITICAL FIX: Add additional wait for edit functionality to be initialized
      // The iframe content might be loaded but edit elements might not be ready yet
      const checkForEditElements = () => {
        const editableElements = iframeDocument.querySelectorAll('.editable-element');
        const taggedImages = iframeDocument.querySelectorAll('img[tag]');
        
        console.log('🎯 TourOverlay: Found', editableElements.length, 'editable elements and', taggedImages.length, 'tagged images');
        
        // If we're looking for editable elements but they don't exist yet, wait a bit more
        if (this.currentStep?.iframeTarget?.includes('editable-element') && editableElements.length === 0) {
          console.log('🎯 TourOverlay: Waiting for editable elements to be initialized...');
          setTimeout(() => {
            this.positionTooltip();
          }, 200);
          return false;
        }
        
        // If we're looking for tagged images but they don't exist yet, wait a bit more  
        if (this.currentStep?.iframeTarget?.includes('img[tag]') && taggedImages.length === 0) {
          console.log('🎯 TourOverlay: Waiting for tagged images to be initialized...');
          setTimeout(() => {
            this.positionTooltip();
          }, 200);
          return false;
        }
        
        return true;
      };
      
      if (!checkForEditElements()) {
        return null;
      }

      // Find the target element inside the iframe
      let iframeTarget = iframeDocument.querySelector(this.currentStep.iframeTarget) as HTMLElement;
      
      // If specific target not found, provide fallbacks based on target type
      if (!iframeTarget) {
        if (this.currentStep.iframeTarget.includes('img')) {
          console.log('🎯 TourOverlay: Specific image target not found, looking for any visible image');
          const images = iframeDocument.querySelectorAll('img');
          for (const img of images) {
            const imgRect = img.getBoundingClientRect();
            // Check if image is visible and has reasonable dimensions
            if (imgRect.width > 50 && imgRect.height > 50 && 
                imgRect.top >= 0 && imgRect.left >= 0) {
              iframeTarget = img as HTMLElement;
              console.log('🎯 TourOverlay: Found fallback image target:', img.src);
              break;
            }
          }
        } else if (this.currentStep.iframeTarget.includes('editable-element')) {
          console.log('🎯 TourOverlay: Specific editable target not found, looking for any text element');
          // Try to find common text elements that might be editable (prioritized order)
          const textSelectors = [
            '[tag]',           // Elements with tag attributes (highest priority)
            'h1', 'h2', 'h3', // Headings (likely to be edited)
            'p',               // Paragraphs
            '.content',        // Content containers
            '.text',           // Text containers
            'h4', 'h5', 'h6'   // Smaller headings
          ];
                      for (const selector of textSelectors) {
              const elements = iframeDocument.querySelectorAll(selector);
              for (const element of elements) {
                const elementRect = element.getBoundingClientRect();
                // Check if element is visible and has text content
                if (elementRect.width > 100 && elementRect.height > 20 && 
                    elementRect.top >= 0 && elementRect.left >= 0 && 
                    element.textContent && element.textContent.trim().length > 5) {
                  
                  // Prefer elements that are more prominently positioned (closer to top-left)
                  if (!iframeTarget || 
                      (elementRect.top < (iframeTarget.getBoundingClientRect().top + 100) && 
                       elementRect.left < (iframeTarget.getBoundingClientRect().left + 100))) {
                    iframeTarget = element as HTMLElement;
                    console.log('🎯 TourOverlay: Found fallback text target:', selector, element.textContent?.substring(0, 50));
                  }
                }
              }
              // If we found a good target with high priority selector, stop searching
              if (iframeTarget && ['[tag]', 'h1', 'h2', 'h3'].includes(selector)) break;
            }
        }
      }
      
      if (!iframeTarget) {
        console.warn('🎯 TourOverlay: No suitable target found in iframe:', this.currentStep.iframeTarget);
        return null;
      }

      // Get the iframe's position relative to the main document
      const iframeRect = iframe.getBoundingClientRect();
      const targetRect = iframeTarget.getBoundingClientRect();

      // Create a virtual element that represents the iframe target's position in the main document
      const virtualElement = document.createElement('div');
      virtualElement.style.position = 'fixed';
      virtualElement.style.top = `${iframeRect.top + targetRect.top}px`;
      virtualElement.style.left = `${iframeRect.left + targetRect.left}px`;
      virtualElement.style.width = `${targetRect.width}px`;
      virtualElement.style.height = `${targetRect.height}px`;
      virtualElement.style.pointerEvents = 'none';
      virtualElement.style.zIndex = '9997';
      virtualElement.classList.add('tour-iframe-target');

      // Remove any existing virtual elements
      document.querySelectorAll('.tour-iframe-target').forEach(el => el.remove());

      // Add the virtual element to the document
      document.body.appendChild(virtualElement);

              console.log('🎯 TourOverlay: Created virtual element for iframe target at:', {
          top: virtualElement.style.top,
          left: virtualElement.style.left,
          width: virtualElement.style.width,
          height: virtualElement.style.height,
          targetType: iframeTarget.tagName,
          targetContent: iframeTarget.textContent?.substring(0, 50) || iframeTarget.getAttribute('src') || 'No content'
        });

      return virtualElement;
    } catch (error) {
      console.error('🎯 TourOverlay: Error accessing iframe content:', error);
      return null;
    }
  }

  /**
   * Create spotlight effect that cuts out the target element area
   */
  private createSpotlight(): void {
    if (!this.targetElement) return;

    const backdrop = document.querySelector('.tour-backdrop') as HTMLElement;
    if (!backdrop) return;

    const targetRect = this.targetElement.getBoundingClientRect();
    const padding = 8; // Extra padding around the target element
    
    // Create spotlight cutout using CSS clip-path
    const spotlightPath = `polygon(
      0% 0%, 
      0% 100%, 
      ${targetRect.left - padding}px 100%, 
      ${targetRect.left - padding}px ${targetRect.top - padding}px, 
      ${targetRect.right + padding}px ${targetRect.top - padding}px, 
      ${targetRect.right + padding}px ${targetRect.bottom + padding}px, 
      ${targetRect.left - padding}px ${targetRect.bottom + padding}px, 
      ${targetRect.left - padding}px 100%, 
      100% 100%, 
      100% 0%
    )`;

    backdrop.style.clipPath = spotlightPath;
    (backdrop.style as any).webkitClipPath = spotlightPath;
  }

  /**
   * Highlight the target element
   */
  private highlightTarget(): void {
    if (!this.targetElement) return;

    // Remove previous highlights
    document.querySelectorAll('.tour-highlight').forEach(el => {
      el.classList.remove('tour-highlight');
    });

    // Add highlight to current target
    this.targetElement.classList.add('tour-highlight');
    
    // Ensure target element is interactive
    this.targetElement.style.pointerEvents = 'auto';
    this.targetElement.style.position = 'relative';
    this.targetElement.style.zIndex = '9998';
  }

  /**
   * Remove highlight from target element
   */
  private removeHighlight(): void {
    document.querySelectorAll('.tour-highlight').forEach(el => {
      el.classList.remove('tour-highlight');
      // Reset inline styles
      (el as HTMLElement).style.pointerEvents = '';
      (el as HTMLElement).style.position = '';
      (el as HTMLElement).style.zIndex = '';
    });

    // Remove virtual iframe target elements
    document.querySelectorAll('.tour-iframe-target').forEach(el => el.remove());

    // Clear backdrop clip-path
    const backdrop = document.querySelector('.tour-backdrop') as HTMLElement;
    if (backdrop) {
      backdrop.style.clipPath = '';
      (backdrop.style as any).webkitClipPath = '';
    }
  }

  /**
   * Handle next step
   */
  onNext(): void {
    this.guidedTourService.nextStep();
  }

  /**
   * Handle previous step
   */
  onPrevious(): void {
    this.guidedTourService.previousStep();
  }

  /**
   * Handle skip tour
   */
  onSkip(): void {
    this.removeHighlight();
    this.guidedTourService.skipTour();
  }

  /**
   * Handle close tour
   */
  onClose(): void {
    this.removeHighlight();
    this.guidedTourService.skipTour();
  }

  /**
   * Get step progress (current step / total steps)
   */
  getStepProgress(): string {
    if (!this.tourState) return '';
    
    const totalSteps = this.tourState.currentPhase === 'initial' ? 5 : 4;
    return `${this.tourState.currentStep + 1} / ${totalSteps}`;
  }

  /**
   * Get progress percentage
   */
  getProgressPercentage(): number {
    if (!this.tourState) return 0;
    
    const totalSteps = this.tourState.currentPhase === 'initial' ? 5 : 4;
    return ((this.tourState.currentStep + 1) / totalSteps) * 100;
  }
} 